import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';

import { Combobox, type ComboOption } from '@/components/ui/combobox';
import { btn, field } from '@/components/ui/styles';
import { useIdentity } from '@/features/identity/identity';
import type { ContactKind, EventRow, LeadDetail } from '@/types/api';
import { COUNTRIES } from '@/utils/countries';
import http from '@/utils/http';
import { sourceLabel } from '@/utils/labels';
import { useApi } from '@/utils/use-api';
import { useClosing } from '@/utils/use-closing';
import { isNoEmail } from '@/utils/format';

// The normalised sources a team member can pick (backend/pkg/api/attribution.go, ATT-1).
// Event comes first: most leads added by hand were met at one.
const SOURCES = ['event', 'google_ads', 'youtube_ads', 'meta_ads', 'tiktok_ads', 'bing_ads', 'reddit_ads', 'chatgpt', 'perplexity', 'claude', 'gemini', 'copilot', 'referral'];

type ContactField = { label: string; placeholder: string; type?: string; inputMode?: 'tel' | 'url' | 'text' };
const CONTACT_FIELDS: Record<ContactKind, ContactField> = {
  phone: { label: 'Phone', placeholder: '+40 712 345 678', type: 'tel', inputMode: 'tel' },
  whatsapp: { label: 'WhatsApp', placeholder: '+55 11 91234 5678', type: 'tel', inputMode: 'tel' },
  instagram: { label: 'Instagram', placeholder: '@handle' },
  linkedin: { label: 'LinkedIn', placeholder: 'linkedin.com/in/…', inputMode: 'url' },
  x: { label: 'X', placeholder: '@handle' },
  discord: { label: 'Discord', placeholder: 'username' },
  website: { label: 'Website', placeholder: 'company.com', inputMode: 'url' },
};
const CONTACTS_SHOWN: ContactKind[] = ['phone', 'whatsapp', 'instagram'];
const CONTACTS_MORE: ContactKind[] = ['linkedin', 'x', 'discord', 'website'];

// The last source, event and country picked by hand, per staff member, preselected the next time.
type LastChoice = { source: string; eventName: string; country: string };
const lastKey = (staffId: string) => `prisma.newLead.last.${staffId || 'anonymous'}`;
const loadLast = (staffId: string): LastChoice | null => {
  try {
    const s = localStorage.getItem(lastKey(staffId));
    return s ? (JSON.parse(s) as LastChoice) : null;
  } catch {
    return null; // storage blocked (private mode, policy)
  }
};
const saveLast = (staffId: string, last: LastChoice) => {
  try {
    localStorage.setItem(lastKey(staffId), JSON.stringify(last));
  } catch {
    /* storage blocked: the form just starts from the defaults next time */
  }
};

type Form = {
  email: string;
  full_name: string;
  company: string;
  telegram_handle: string;
  country: string;
  source: string;
  event: string; // an event id
  contacts: Partial<Record<ContactKind, string>>;
  notes: string;
};

type Problem = { message: string; field?: string };

const Label = ({ htmlFor, children }: { htmlFor: string; children: ReactNode }) => (
  <label className={field.label} htmlFor={htmlFor}>
    {children}
  </label>
);

const FieldError = ({ problem, name }: { problem: Problem | null; name: string }) =>
  problem?.field === name ? (
    <p role="alert" className="mt-1 text-sm text-danger">
      {problem.message}
    </p>
  ) : null;

/**
 * Add a lead by hand, or edit one (LEAD-7). The email is the lead's identity: one that
 * is already a lead opens that lead instead (LEAD-3), and it never changes. A lead can
 * be added without one, with another way to reach them; it then takes its email once,
 * when the team has it. On a lead with an Owner Hub account, the name, company and
 * country come from Owner Hub.
 *
 * Adding remembers the last source, event and country picked and preselects them;
 * Event is the default the first time. A new event's name is written once and is a
 * choice from then on, the newest events first. Full screen on a phone (UI-1).
 */
const LeadForm = ({
  lead,
  onClose,
  onSaved,
}: {
  lead?: LeadDetail;
  onClose: () => void;
  /** created is false when the email was already a lead and the user chose to open it. */
  onSaved: (id: string, created: boolean) => void;
}) => {
  const [closing, close] = useClosing(onClose);
  const editing = Boolean(lead);
  const noEmail = Boolean(lead && isNoEmail(lead.email));
  const fromOwnerHub = Boolean(lead?.owner_user_id);
  const { me } = useIdentity();
  const staffId = me?.id ?? '';
  const events = useApi<EventRow[]>(editing ? null : '/events');

  const [form, setForm] = useState<Form>(() => {
    if (lead) {
      return {
        email: isNoEmail(lead.email) ? '' : lead.email,
        full_name: lead.full_name,
        company: lead.company,
        telegram_handle: lead.telegram_handle ? `@${lead.telegram_handle}` : '',
        country: lead.country,
        source: '',
        event: '',
        contacts: { ...lead.contacts },
        notes: lead.notes,
      };
    }
    const last = loadLast(staffId);
    return {
      email: '',
      full_name: '',
      company: '',
      telegram_handle: '',
      country: last?.country ?? '',
      source: last?.source ?? 'event',
      event: '',
      contacts: {},
      notes: '',
    };
  });
  const [moreContacts, setMoreContacts] = useState(() => CONTACTS_MORE.some((k) => lead?.contacts[k]));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [existing, setExisting] = useState<string | null>(null);

  // Events made in this dialog come first: they are the newest.
  const [madeEvents, setMadeEvents] = useState<EventRow[]>([]);
  const allEvents = useMemo(() => {
    const seen = new Set(madeEvents.map((e) => e.id));
    return [...madeEvents, ...(events.data ?? []).filter((e) => !seen.has(e.id))];
  }, [madeEvents, events.data]);
  const eventOptions: ComboOption[] = allEvents.map((e) => ({ value: e.id, label: e.name, hint: e.leads === 1 ? '1 lead' : `${e.leads} leads` }));
  const countryOptions: ComboOption[] = useMemo(() => COUNTRIES.map((c) => ({ value: c.code3, label: c.name, keywords: `${c.code3} ${c.code2}` })), []);

  // Once the events are in: preselect the event picked last time, else the newest one.
  useEffect(() => {
    if (editing || !events.data || form.event) return;
    const lastName = loadLast(staffId)?.eventName.toLowerCase();
    const match = events.data.find((e) => e.name.toLowerCase() === lastName) ?? events.data[0];
    if (match) setForm((f) => ({ ...f, event: match.id }));
    // Only the arrival of the events decides this.
  }, [events.data]);

  // A new event is saved as soon as it is named, and is a choice for everyone from then on.
  const createEvent = async (name: string): Promise<ComboOption | null> => {
    try {
      const res = await http.post<EventRow>('/events', { name });
      setMadeEvents((list) => [res.data, ...list.filter((e) => e.id !== res.data.id)]);
      setProblem(null);
      return { value: res.data.id, label: res.data.name };
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setProblem({ field: 'event', message: msg ? msg.charAt(0).toUpperCase() + msg.slice(1) + '.' : 'The event could not be saved. Try again.' });
      return null;
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const set = (k: keyof Omit<Form, 'contacts'>) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setContact = (k: ContactKind) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, contacts: { ...f.contacts, [k]: e.target.value } }));

  const isEvent = !editing && form.source === 'event';
  const eventName = allEvents.find((e) => e.id === form.event)?.name ?? '';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (isEvent && !eventName) {
      setProblem({ field: 'event', message: 'Pick the event, or type a new one and create it.' });
      return;
    }
    setBusy(true);
    setProblem(null);
    setExisting(null);
    const details = {
      full_name: form.full_name,
      company: form.company,
      country: form.country,
      telegram_handle: form.telegram_handle,
      contacts: form.contacts,
      notes: form.notes,
    };
    try {
      if (lead) {
        // A lead added without an email takes its real one here, once (LEAD-7).
        await http.put(`/leads/${encodeURIComponent(lead.id)}`, { ...details, ...(noEmail && form.email.trim() ? { email: form.email } : {}) });
        onSaved(lead.id, true);
      } else {
        const res = await http.post<{ id: string }>('/leads', {
          ...details,
          email: form.email,
          source: form.source,
          ...(isEvent ? { event_id: form.event } : {}),
        });
        saveLast(staffId, { source: form.source, eventName, country: form.country });
        onSaved(res.data.id, true);
      }
    } catch (err: unknown) {
      const r = (err as { response?: { status?: number; data?: { lead_id?: string; error?: string; field?: string } } }).response;
      if (r?.status === 409 && r.data?.lead_id) {
        setExisting(r.data.lead_id);
        setProblem({ message: 'A lead with this email already exists.' });
      } else if (r?.status === 422 && r.data?.error) {
        const message = r.data.error.charAt(0).toUpperCase() + r.data.error.slice(1) + '.';
        setProblem({ message, field: r.data.field });
      } else {
        setProblem({ message: 'That did not work. Try again.' });
      }
    } finally {
      setBusy(false);
    }
  };

  const ownerHubNote = fromOwnerHub && <p className="mt-1 text-xs text-fg-faint">From Owner Hub</p>;

  // Tab stays inside the dialog while it is open.
  const dialogRef = useRef<HTMLDivElement>(null);
  const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    // The overlay blocks the rest of the app; a click on it does nothing, so a misclick
    // never loses what was typed. Close with Cancel, the cross or Escape.
    <div className={`fixed inset-0 z-50 flex items-stretch justify-center bg-overlay md:items-center md:p-6 ${closing ? 'animate-fade-out' : 'animate-fade-in'}`}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lf-title"
        onKeyDown={trapTab}
        className={`flex h-full w-full flex-col bg-surface-raised pt-[env(safe-area-inset-top)] shadow-e4 md:h-auto md:max-h-[min(90vh,860px)] md:max-w-[560px] md:overflow-hidden md:rounded-card md:pt-0 ${closing ? 'animate-sheet-out md:animate-pop-out' : 'animate-sheet-in md:animate-pop-in'}`}
      >
      <div className="flex items-center gap-3 py-3 pl-4 pr-2 md:px-5 md:py-4">
        <h2 id="lf-title" className="flex-1 truncate text-lg font-semibold text-fg-strong">{editing ? 'Edit lead' : 'New lead'}</h2>
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-fg-muted hover:bg-surface-hover hover:text-fg md:h-8 md:w-8"
        >
          <svg viewBox="0 0 16 16" className="h-5 w-5 md:h-4 md:w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto border-t border-line px-4 py-4 md:px-5">
          <div>
            <Label htmlFor="lf-email">Email</Label>
            {editing && !noEmail ? (
              <p className="break-all text-base text-fg-strong md:text-sm">{form.email}</p>
            ) : (
              <input id="lf-email" className={field.input} type="email" inputMode="email" autoComplete="off" autoFocus={!editing} value={form.email} onChange={set('email')} placeholder="name@company.com" />
            )}
            <p className="mt-1 text-xs text-fg-faint">
              {editing && !noEmail
                ? 'The email identifies the lead and does not change (LEAD-3).'
                : editing
                  ? 'Added without an email. When you have it, it is set here, once (LEAD-7).'
                  : 'The email identifies the lead (LEAD-3). Without one, give another way to reach them: Telegram, a phone…'}
            </p>
            <FieldError problem={problem} name="email" />
          </div>

          {!editing && (
            <div>
              <Label htmlFor="lf-source">Where they came from</Label>
              <select id="lf-source" className={`${field.select} w-full`} value={form.source} onChange={set('source')}>
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {sourceLabel(s)}
                  </option>
                ))}
                <option value="">Unknown</option>
              </select>
              <FieldError problem={problem} name="source" />
            </div>
          )}

          {isEvent && (
            <div>
              <Label htmlFor="lf-event">Event</Label>
              <Combobox
                id="lf-event"
                value={form.event}
                options={eventOptions}
                onChange={(v) => setForm((f) => ({ ...f, event: v }))}
                onCreate={createEvent}
                createLabel={(text) => `Create event “${text}”`}
                placeholder="Search, or type a new one, e.g. ICE Barcelona 2027"
                loading={!events.data && !events.error}
              />
              <FieldError problem={problem} name="event" />
            </div>
          )}

          <div>
            <Label htmlFor="lf-name">Name</Label>
            <input id="lf-name" className={field.input} autoComplete="off" value={form.full_name} onChange={set('full_name')} disabled={fromOwnerHub} />
            {ownerHubNote}
            <FieldError problem={problem} name="full_name" />
          </div>
          <div>
            <Label htmlFor="lf-company">Company</Label>
            <input id="lf-company" className={field.input} autoComplete="off" value={form.company} onChange={set('company')} disabled={fromOwnerHub} />
            {ownerHubNote}
            <FieldError problem={problem} name="company" />
          </div>
          <div>
            <Label htmlFor="lf-country">Country</Label>
            <Combobox
              id="lf-country"
              value={form.country}
              options={countryOptions}
              onChange={(v) => setForm((f) => ({ ...f, country: v }))}
              clearLabel="Unknown"
              placeholder="Unknown"
              disabled={fromOwnerHub}
            />
            {ownerHubNote}
            <FieldError problem={problem} name="country" />
          </div>
          <FieldError problem={problem} name="owner_hub" />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="lf-telegram">Telegram</Label>
              <input id="lf-telegram" className={field.input} autoComplete="off" autoCapitalize="none" value={form.telegram_handle} onChange={set('telegram_handle')} placeholder="@handle" />
              <FieldError problem={problem} name="telegram_handle" />
            </div>
            {(moreContacts ? [...CONTACTS_SHOWN, ...CONTACTS_MORE] : CONTACTS_SHOWN).map((k) => (
              <div key={k}>
                <Label htmlFor={`lf-${k}`}>{CONTACT_FIELDS[k].label}</Label>
                <input
                  id={`lf-${k}`}
                  className={field.input}
                  type={CONTACT_FIELDS[k].type ?? 'text'}
                  inputMode={CONTACT_FIELDS[k].inputMode}
                  autoComplete="off"
                  autoCapitalize="none"
                  value={form.contacts[k] ?? ''}
                  onChange={setContact(k)}
                  placeholder={CONTACT_FIELDS[k].placeholder}
                />
                <FieldError problem={problem} name={k} />
              </div>
            ))}
          </div>
          {!moreContacts && (
            <button type="button" className={`${btn.quiet} -ml-3`} onClick={() => setMoreContacts(true)}>
              More contacts: LinkedIn, X, Discord, website
            </button>
          )}

          <div>
            <Label htmlFor="lf-notes">Notes</Label>
            <textarea id="lf-notes" className={field.textarea} rows={4} maxLength={2000} value={form.notes} onChange={set('notes')} placeholder="How you met, what they asked" />
            <FieldError problem={problem} name="notes" />
          </div>

          {problem && !problem.field && (
            <div role="alert" className="text-sm text-danger">
              {problem.message}
              {existing && (
                <button type="button" className={`${btn.link} ml-2 inline-flex min-h-11 items-center md:min-h-0`} onClick={() => onSaved(existing, false)}>
                  Open the existing lead
                </button>
              )}
            </div>
          )}
        </div>
        <div className="relative z-10 flex gap-2 bg-surface px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-up md:px-5">
          <button type="button" className={`${btn.secondary} flex-1 md:flex-none`} onClick={close}>
            Cancel
          </button>
          <button type="submit" className={`${btn.primary} flex-1 md:ml-auto md:flex-none`} disabled={busy}>
            {busy ? 'Saving…' : editing ? 'Save' : 'Add lead'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

/** The Leads page's "New lead" panel. */
const NewLeadPanel = ({ onClose, onOpen }: { onClose: () => void; onOpen: (id: string, created: boolean) => void }) => (
  <LeadForm onClose={onClose} onSaved={onOpen} />
);

/** Edit a lead, from its panel or its page. */
export const EditLeadPanel = ({ lead, onClose, onSaved }: { lead: LeadDetail; onClose: () => void; onSaved: () => void }) => (
  <LeadForm lead={lead} onClose={onClose} onSaved={() => onSaved()} />
);

export default NewLeadPanel;
